import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useUserCartItems from "api/CartResource/useUserCartItems";
import useFetchCurrencyByCode from "api/RatesResource/useFetchCurrencyByCode";
import useGetUserShippingAddresses from "api/AddressResource/useGetUserShippingAddresses";
import { BookInCart, Empty, ModalNotification } from "components";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { AiOutlineCheckCircle, AiOutlinePlusCircle } from "react-icons/ai";
import { BiCheck } from "react-icons/bi";
import { FaGlobe, FaPaypal, FaStar, FaTicketAlt, FaArrowLeft, FaArrowRight, FaGift } from "react-icons/fa";
import usePrice from "hooks/usePrice";
import useCurrencyStore from "store/currencyStore";
import { activeCurrencies, activePaymentMethods, getMoneyPrefix } from "utils";
import { BookFormat } from "utils/server-constants/bookFormats";
import EcocashPayment from "./EcocashPayment";
import EcocashPaymentUsd from "./EcocashPaymentUsd";
import PayPalPayment from "./PayPalPayment";
import RedeemStars from "./RedeemStars";
import VoucherPayment from "./VoucherPayment";
import ShippingDetails from "./ShippingDetails";
import ShippingAddressToUse from "./ShippingAddressToUse";
import CheckoutPageLoader from "./CheckoutPageLoader";
import RecipientSearch from "./RecipientSearch";

const Checkout = () => {
	const { currency, paymentMethod, setPaymentMethod, setCurrency } = useCurrencyStore();
	const { data: currencyRate, isLoading: isLoadingCurrency } = useFetchCurrencyByCode(currency);

	const [showShippingDetails, setShowShippingDetails] = useState(false);
	const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
	const { data: shippingAddresses, isLoading: isLoadingShippingAddresses } =
		useGetUserShippingAddresses();

	const { data: cart, isLoading: previewIsLoading } = useUserCartItems();

	const [showPaymentCompletedModal, setShowPaymentCompletedModal] = useState(false);
	const [paymentIsSuccessful, setPaymentIsSuccessful] = useState(false);
	const [totalPaid, setTotalPaid] = useState(0);
	const [bookCount, setBookCount] = useState(0);
	const [bookItems, setBookItems] = useState(null);
	const [showAddNewDeliveryAddress, setShowAddNewDeliveryAddress] = useState(false);
	// const [showToast, setShowToast] = useState(false);
	// const [toastMessage, setToastMessage] = useState("");
	const [prevCurrency, setPrevCurrency] = useState(currency);
	const [showMobileOrderSummary, setShowMobileOrderSummary] = useState(false);

	// Step-based checkout flow for physical books
	// Step 1: Address, Step 2: Payment
	const [currentStep, setCurrentStep] = useState(1);

	// Gift to someone else
	const [buyForSomeoneElse, setBuyForSomeoneElse] = useState(false);
	const [selectedRecipient, setSelectedRecipient] = useState(null);

	// All hooks must be called before any conditional logic
	// useEffect for detecting physical books
	useEffect(() => {
		if (cart?.cart_items) {
			const hasPhysicalBooks = Boolean(
				cart.cart_items.find((item) => item.format === BookFormat.Physical),
			);
			setShowShippingDetails(hasPhysicalBooks);
		}
	}, [cart]);

	// Check if all books in cart are digital (required for gifting)
	const allBooksAreDigital = React.useMemo(() => {
		if (!cart?.cart_items || cart.cart_items.length === 0) return false;
		return cart.cart_items.every((item) => item.format === BookFormat.Digital);
	}, [cart]);

	// Reset recipient when toggling off
	const handleToggleGift = (enabled) => {
		setBuyForSomeoneElse(enabled);
		if (!enabled) {
			setSelectedRecipient(null);
		}
	};

	const subtotal = usePrice({
		sellable: true,
		usdPrice: cart?.grand_total,
		zwlPrice: cart?.grand_total_zwl,
		isFree: false,
	});

	const deliveryFeePerMeter = usePrice({
		sellable: true,
		usdPrice: cart?.delivery_fee_per_meter?.[activeCurrencies.USD.code],
		zwlPrice: cart?.delivery_fee_per_meter?.[activeCurrencies.ZWL.code],
		isFree: false,
	});

	const deliveryFee = React.useMemo(() => {
		if (!cart?.cart_items?.some((item) => item.format === BookFormat.Physical)) {
			return 0;
		}

		if (!selectedShippingAddress) return 0;
		const distance = selectedShippingAddress?.distance_in_meters || 0;
		return distance * deliveryFeePerMeter;
	}, [cart, selectedShippingAddress, deliveryFeePerMeter]);

	// Calculate grand total in both currencies
	const grandTotalUSD = React.useMemo(() => {
		const subtotalUSD = cart?.grand_total || 0;
		const deliveryFeeUSD = !cart?.cart_items?.some((item) => item.format === BookFormat.Physical)
			? 0
			: (selectedShippingAddress?.distance_in_meters || 0) *
				(cart?.delivery_fee_per_meter?.[activeCurrencies.USD.code] || 0);
		return (Number(deliveryFeeUSD) + Number(subtotalUSD)).toFixed(2);
	}, [cart, selectedShippingAddress]);

	const grandTotalZWL = React.useMemo(() => {
		const subtotalZWL = cart?.grand_total_zwl || 0;
		const deliveryFeeZWL = !cart?.cart_items?.some((item) => item.format === BookFormat.Physical)
			? 0
			: (selectedShippingAddress?.distance_in_meters || 0) *
				(cart?.delivery_fee_per_meter?.[activeCurrencies.ZWL.code] || 0);
		return (Number(deliveryFeeZWL) + Number(subtotalZWL)).toFixed(2);
	}, [cart, selectedShippingAddress]);

	// Use the appropriate grand total based on selected currency
	const grandTotal = currency === activeCurrencies.USD.code ? grandTotalUSD : grandTotalZWL;

	const credits = React.useMemo(() => {
		return cart?.cart_items?.reduce(
			(prevValue, currentValue) =>
				prevValue + Number(currentValue.credits) * Number(currentValue.quantity),
			0,
		);
	}, [cart]);

	// Filter payment methods based on selected currency
	const paymentOptionsButtons = React.useMemo(() => {
		const allPaymentOptionsButtons = [
			{
				paymentMethod: activePaymentMethods.ecocashUsd.name,
				label: activePaymentMethods.ecocashUsd.label,
				icon: <FaGlobe size={22} className="text-green-600" />,
				supportedCurrencies: [activeCurrencies.USD.code],
				processingTime: "2-3 mins",
				description: "Pay with EcoCash USD wallet",
				iconBg: "bg-green-50",
				recommended: false,
			},
			{
				paymentMethod: activePaymentMethods.ecocashZwl.name,
				label: activePaymentMethods.ecocashZwl.label,
				icon: <FaGlobe size={22} className="text-green-600" />,
				supportedCurrencies: [activeCurrencies.ZWL.code],
				processingTime: "2-3 mins",
				description: "Pay with EcoCash ZWL wallet",
				iconBg: "bg-green-50",
				recommended: true,
			},
			{
				paymentMethod: activePaymentMethods.paypal.name,
				label: activePaymentMethods.paypal.label,
				icon: <FaPaypal size={22} className="text-akgreen" />,
				supportedCurrencies: [activeCurrencies.USD.code],
				processingTime: "Instant",
				description: "Secure payment via PayPal",
				iconBg: "bg-akgreen/10",
				recommended: true,
			},
			{
				paymentMethod: activePaymentMethods.stars.name,
				label: activePaymentMethods.stars.label,
				icon: <FaStar size={22} className="text-yellow-500" />,
				supportedCurrencies: [activeCurrencies.ZWL.code],
				processingTime: "Instant",
				description: "Use your Akello Smart Learning stars",
				iconBg: "bg-yellow-50",
				recommended: false,
			},
			{
				paymentMethod: activePaymentMethods.voucher.name,
				label: activePaymentMethods.voucher.label,
				icon: <FaTicketAlt size={22} className="text-purple-600" />,
				supportedCurrencies: [activeCurrencies.USD.code, activeCurrencies.ZWL.code],
				processingTime: "Instant",
				description: "Redeem a prepaid voucher code",
				iconBg: "bg-purple-50",
				recommended: false,
			},
		];

		return allPaymentOptionsButtons.filter((option) =>
			option.supportedCurrencies.includes(currency),
		);
	}, [currency]);

	// Show toast notification when currency changes
	useEffect(() => {
		// if (prevCurrency && prevCurrency !== currency) {
		// const currencyLabel =
		// 	currency === activeCurrencies.USD.code
		// 		? activeCurrencies.USD.label
		// 		: activeCurrencies.ZWL.label;
		// setToastMessage(`Switched to ${currencyLabel} - showing compatible payment methods`);
		// setShowToast(true);
		// }
		setPrevCurrency(currency);
	}, [currency, prevCurrency]);

	// Auto-select first available payment method when currency changes
	useEffect(() => {
		const currentMethodSupported = paymentOptionsButtons.some(
			(option) => option.paymentMethod === paymentMethod,
		);

		if (!currentMethodSupported && paymentOptionsButtons.length > 0) {
			setPaymentMethod(paymentOptionsButtons[0].paymentMethod);
		}
	}, [currency, paymentOptionsButtons, paymentMethod, setPaymentMethod]);

	const isLoading = previewIsLoading || isLoadingCurrency || isLoadingShippingAddresses;
	const hasNoItems = cart?.cart_items?.length <= 0;

	// ============================================
	// DERIVED VALUES AND HELPER FUNCTIONS
	// (Must be after all hooks)
	// ============================================

	const hasSavedShippingAddresses = shippingAddresses?.length > 0;

	// Determine the effective address - either selected or the first saved one
	const effectiveAddress = selectedShippingAddress || shippingAddresses?.[0];

	// Check if user can proceed to payment step
	const canProceedToPayment = !showShippingDetails || Boolean(effectiveAddress);

	// Address ID for payment processing
	const addressId = showShippingDetails
		? selectedShippingAddress?.id || shippingAddresses?.[0]?.id
		: null;

	// Books to buy for payment processing
	const booksToBuy = cart?.cart_items?.map((cartItem) => ({
		book_id: cartItem.book_id,
		quantity: cartItem.quantity,
		format: cartItem.format,
	}));

	// Navigate to next step with validation
	const goToNextStep = () => {
		if (currentStep === 1 && canProceedToPayment) {
			setCurrentStep(2);
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	// Navigate to previous step
	const goToPreviousStep = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	};

	// Handle step click in progress indicator
	const handleStepClick = (step) => {
		if (step === 1) {
			setCurrentStep(1);
		} else if (step === 2 && canProceedToPayment) {
			setCurrentStep(2);
		}
	};

	const recipientUserId = selectedRecipient?.id;

	const renderPaymentContent = (method) => {
		if (method === activePaymentMethods.stars.name && currency === activeCurrencies.ZWL.code) {
			return (
				<RedeemStars
					credits={credits}
					grandTotal={Number(grandTotal)}
					books={booksToBuy}
					setBookCount={setBookCount}
					setTotalPaid={setTotalPaid}
					setBookItems={setBookItems}
					setPaymentIsSuccessful={setPaymentIsSuccessful}
					addressId={addressId}
					recipientUserId={recipientUserId}
				/>
			);
		}

		if (method === activePaymentMethods.ecocashZwl.name && currency === activeCurrencies.ZWL.code) {
			return (
				<EcocashPayment
					grandTotal={Number(grandTotal)}
					books={booksToBuy}
					setPaymentIsSuccessful={setPaymentIsSuccessful}
					addressId={addressId}
					recipientUserId={recipientUserId}
				/>
			);
		}

		if (method === activePaymentMethods.ecocashUsd.name) {
			return (
				<EcocashPaymentUsd
					grandTotal={Number(grandTotal)}
					books={booksToBuy}
					setPaymentIsSuccessful={setPaymentIsSuccessful}
					addressId={addressId}
					recipientUserId={recipientUserId}
				/>
			);
		}

		if (method === activePaymentMethods.paypal.name && currency === activeCurrencies.USD.code) {
			return (
				<PayPalPayment
					grandTotal={Number(grandTotal)}
					setPaymentIsSuccessful={setPaymentIsSuccessful}
					books={booksToBuy}
					addressId={addressId}
					recipientUserId={recipientUserId}
				/>
			);
		}

		if (method === activePaymentMethods.voucher.name) {
			return (
				<VoucherPayment
					grandTotal={Number(grandTotal)}
					books={booksToBuy}
					setPaymentIsSuccessful={setPaymentIsSuccessful}
					addressId={addressId}
					recipientUserId={recipientUserId}
				/>
			);
		}

		return (
			<div className="p-4 text-center text-gray-500">
				This payment method is not available with the selected currency.
			</div>
		);
	};

	if (paymentIsSuccessful) {
		return (
			<Container>
				<div className="min-h-[50vh] flex items-center justify-center py-8">
					<div className="mx-auto max-w-sm w-full px-4">
						{/* Success Card */}
						<div className="bg-white rounded-lg border border-gray-200 overflow-hidden text-center">
							{/* Success Icon */}
							<div className="pt-6 pb-3">
								<div className="mx-auto w-12 h-12 rounded-full bg-akgreen/10 flex items-center justify-center mb-3">
									<BiCheck size={28} className="text-akgreen" />
								</div>
								<h2 className="text-lg font-semibold text-gray-900 mb-0.5">Payment Successful</h2>
								<p className="text-gray-500 text-sm">Your books are now in your library</p>
							</div>

							{/* Actions */}
							<div className="px-5 pb-5 space-y-2">
								<Link
									to="/my-library"
									className="w-full flex items-center justify-center gap-2 bg-akgreen text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-akgreener transition-colors"
								>
									Go to My Library
									<FaArrowRight className="w-3 h-3" />
								</Link>

								<Link
									to="/books"
									className="w-full flex items-center justify-center text-gray-500 hover:text-gray-700 text-sm py-1.5"
								>
									Continue browsing
								</Link>
							</div>

							{/* Support Link */}
							<div className="border-t px-5 py-2.5 bg-gray-50">
								<p className="text-xs text-gray-500">
									Need help?{" "}
									<Link to="/support" className="text-akgreen hover:underline">
										Contact support
									</Link>
								</p>
							</div>
						</div>
					</div>
				</div>
			</Container>
		);
	}

	if (isLoading) {
		return <CheckoutPageLoader />;
	}

	if (hasNoItems) {
		return (
			<Container>
				<div className="mt-8 flex-1">
					<PaddingX>
						<div className="max-w-sm mx-auto">
							<Empty cartIconSize={120}>
								<div className="flex flex-col items-center gap-4">
									<p className="text-base text-gray-600">Your cart is empty</p>
									<Link
										to="/books"
										className="bg-akgreen px-6 py-2.5 text-sm text-white rounded-md hover:bg-akgreener transition-colors"
									>
										Continue shopping
									</Link>
								</div>
							</Empty>
						</div>
					</PaddingX>
				</div>
			</Container>
		);
	}

	return (
		<Container>
			<div className="mt-4 md:mt-8 flex-1 pb-24 lg:pb-8">
				<PaddingX>
					{/* Centered Content Wrapper */}
					<div className="max-w-3xl mx-auto">
						{/* Progress Steps */}
						{showShippingDetails && (
							<div className="mb-8 flex justify-center">
								<div className="flex items-center">
									{/* Step 1: Delivery */}
									<button
										type="button"
										onClick={() => handleStepClick(1)}
										className="flex flex-col items-center group cursor-pointer"
									>
										<div className="relative">
											<div
												className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
													currentStep > 1
														? "bg-akgreen text-white"
														: currentStep === 1
															? "bg-akgreen text-white ring-4 ring-akgreen/20"
															: "bg-gray-100 text-gray-400"
												}`}
											>
												{currentStep > 1 ? (
													<BiCheck size={20} />
												) : (
													<svg
														className="w-5 h-5"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
														/>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
														/>
													</svg>
												)}
											</div>
										</div>
										<span
											className={`mt-2 text-xs font-medium transition-colors ${
												currentStep >= 1 ? "text-gray-900" : "text-gray-400"
											}`}
										>
											Delivery
										</span>
									</button>

									{/* Connector Line */}
									<div className="w-16 sm:w-24 h-[2px] mx-2 relative">
										<div className="absolute inset-0 bg-gray-200 rounded-full" />
										<div
											className={`absolute inset-y-0 left-0 bg-akgreen rounded-full transition-all duration-500 ease-out ${
												currentStep > 1 ? "w-full" : "w-0"
											}`}
										/>
									</div>

									{/* Step 2: Payment */}
									<button
										type="button"
										onClick={() => handleStepClick(2)}
										disabled={!canProceedToPayment}
										className={`flex flex-col items-center group ${
											canProceedToPayment ? "cursor-pointer" : "cursor-not-allowed"
										}`}
									>
										<div className="relative">
											<div
												className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
													currentStep > 2
														? "bg-akgreen text-white"
														: currentStep === 2
															? "bg-akgreen text-white ring-4 ring-akgreen/20"
															: canProceedToPayment
																? "bg-gray-100 text-gray-400 group-hover:bg-gray-200"
																: "bg-gray-50 text-gray-300"
												}`}
											>
												{currentStep > 2 ? (
													<BiCheck size={20} />
												) : (
													<svg
														className="w-5 h-5"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
														/>
													</svg>
												)}
											</div>
										</div>
										<span
											className={`mt-2 text-xs font-medium transition-colors ${
												currentStep >= 2
													? "text-gray-900"
													: canProceedToPayment
														? "text-gray-400 group-hover:text-gray-600"
														: "text-gray-300"
											}`}
										>
											Payment
										</span>
									</button>
								</div>
							</div>
						)}

						{/* Main Content Grid */}
						<div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
							{/* Main Step Content */}
							<div className="lg:col-span-3">
								{/* STEP 1: Shipping Address (only for physical books) */}
								{showShippingDetails && currentStep === 1 && (
									<div className="bg-white rounded-lg border border-gray-200 p-5">
										<h2 className="text-base font-semibold text-gray-900 mb-4">Delivery Address</h2>

										<div className="space-y-6">
											{/* Saved addresses selection */}
											{hasSavedShippingAddresses && !showAddNewDeliveryAddress && (
												<div className="space-y-4">
													<p className="text-gray-600">
														Select a delivery address or add a new one:
													</p>
													<ShippingAddressToUse
														selectedShippingAddress={selectedShippingAddress}
														setSelectedShippingAddress={setSelectedShippingAddress}
														shippingAddresses={shippingAddresses}
													/>
												</div>
											)}

											{/* Add new address button */}
											{!showAddNewDeliveryAddress && hasSavedShippingAddresses && (
												<button
													className="flex items-center gap-2 text-akgreen hover:text-akgreener transition-colors duration-300"
													onClick={() => setShowAddNewDeliveryAddress(true)}
												>
													<AiOutlinePlusCircle size={25} />
													<span className="font-medium">Add new delivery address</span>
												</button>
											)}

											{/* New address form */}
											{(showAddNewDeliveryAddress || !hasSavedShippingAddresses) && (
												<div className="space-y-4">
													{!hasSavedShippingAddresses && (
														<p className="text-gray-600">
															Please add a delivery address to continue:
														</p>
													)}
													<ShippingDetails
														setShowAddNewDeliveryAddress={setShowAddNewDeliveryAddress}
													/>
												</div>
											)}

											{/* Continue Button */}
											<div className="pt-4 border-t mt-4">
												<button
													type="button"
													onClick={goToNextStep}
													disabled={!canProceedToPayment}
													className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-colors ${
														canProceedToPayment
															? "bg-akgreen text-white hover:bg-akgreener"
															: "bg-gray-200 text-gray-400 cursor-not-allowed"
													}`}
												>
													<span>Continue to Payment</span>
													<FaArrowRight className="w-3.5 h-3.5" />
												</button>
												{!canProceedToPayment && (
													<p className="text-xs text-red-500 mt-2 text-center">
														Please select or add a delivery address
													</p>
												)}
											</div>
										</div>
									</div>
								)}

								{/* STEP 2: Payment (shown for physical books on step 2, or directly for digital-only) */}
								{(!showShippingDetails || currentStep === 2) && (
									<div className="bg-white rounded-lg border border-gray-200 p-5">
										{/* Selected Address Summary (only for physical books) */}
										{showShippingDetails && effectiveAddress && (
											<div className="mb-4 pb-3 border-b flex items-start justify-between">
												<div className="text-sm">
													<p className="text-gray-500 text-xs mb-0.5">Delivering to</p>
													<p className="font-medium text-gray-900">
														{effectiveAddress.recipient_name}
													</p>
													<p className="text-gray-500 text-xs">{effectiveAddress.city}</p>
												</div>
												<button
													type="button"
													onClick={goToPreviousStep}
													className="text-akgreen text-xs font-medium"
												>
													Change
												</button>
											</div>
										)}

										{/* Buy for someone else - only for digital books */}
										{allBooksAreDigital && (
											<div className="mb-4 pb-4 border-b">
												<label className="flex items-center gap-3 cursor-pointer">
													<input
														type="checkbox"
														checked={buyForSomeoneElse}
														onChange={(e) => handleToggleGift(e.target.checked)}
														className="w-4 h-4 text-akgreen border-gray-300 rounded focus:ring-akgreen"
													/>
													<span className="flex items-center gap-2 text-sm font-medium text-gray-900">
														<FaGift className="text-akgreen" />
														Buy for someone else
													</span>
												</label>
												<p className="text-xs text-gray-500 mt-1 ml-7">
													The books will be added to the recipient's library
												</p>

												{buyForSomeoneElse && (
													<div className="mt-3 ml-7">
														<RecipientSearch
															selectedRecipient={selectedRecipient}
															onSelectRecipient={setSelectedRecipient}
															onClearRecipient={() => setSelectedRecipient(null)}
														/>
													</div>
												)}
											</div>
										)}

										<h2 className="text-base font-semibold text-gray-900 mb-4">Payment</h2>

										<div className="space-y-4">
											{/* Currency Selector */}
											<div className="mb-4 md:mb-6">
												<label className="block text-sm font-medium text-gray-700 mb-2">
													Currency
												</label>
												<div className="inline-flex rounded-full p-1 bg-gray-100">
													<button
														type="button"
														onClick={() => setCurrency(activeCurrencies.USD.code)}
														className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
															currency === activeCurrencies.USD.code
																? "bg-akgreen text-white"
																: "text-gray-700 hover:text-gray-900"
														}`}
													>
														{activeCurrencies.USD.label}
													</button>
													<button
														type="button"
														onClick={() => setCurrency(activeCurrencies.ZWL.code)}
														className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
															currency === activeCurrencies.ZWL.code
																? "bg-akgreen text-white"
																: "text-gray-700 hover:text-gray-900"
														}`}
													>
														{activeCurrencies.ZWL.label}
													</button>
												</div>
											</div>

											{paymentOptionsButtons.length > 0 ? (
												<div className="space-y-3">
													{/* Payment Method Pills */}
													<div>
														<p className="text-xs font-medium text-gray-600 mb-1.5">
															Payment method
														</p>
														<div className="flex flex-wrap gap-1.5">
															{paymentOptionsButtons.map((button) => {
																const isSelected = paymentMethod === button.paymentMethod;
																return (
																	<button
																		key={button.paymentMethod}
																		type="button"
																		onClick={() => setPaymentMethod(button.paymentMethod)}
																		className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
																			isSelected
																				? "border-akgreen bg-akgreen/5 text-akgreen"
																				: "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
																		}`}
																	>
																		<span className="w-4 h-4 flex items-center justify-center [&>svg]:w-4 [&>svg]:h-4">
																			{button.icon}
																		</span>
																		<span>{button.label}</span>
																	</button>
																);
															})}
														</div>
													</div>

													{/* Payment Form */}
													<div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
														{(() => {
															const selectedOption = paymentOptionsButtons.find(
																(o) => o.paymentMethod === paymentMethod,
															);
															return (
																<>
																	{selectedOption && (
																		<p className="text-xs text-gray-600 mb-3">
																			{selectedOption.description} • {selectedOption.processingTime}
																		</p>
																	)}
																	{renderPaymentContent(paymentMethod)}
																</>
															);
														})()}
													</div>
												</div>
											) : (
												<div className="text-center py-6 text-gray-500 text-sm">
													<p>No payment methods available for this currency.</p>
												</div>
											)}

											{/* Back button for physical books */}
											{showShippingDetails && (
												<div className="pt-3 border-t">
													<button
														type="button"
														onClick={goToPreviousStep}
														className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm transition-colors"
													>
														<FaArrowLeft className="w-3 h-3" />
														<span>Back</span>
													</button>
												</div>
											)}
										</div>
									</div>
								)}
							</div>

							{/* Order Summary Sidebar */}
							<div className="hidden lg:block lg:col-span-2">
								<div className="bg-gray-50 rounded-lg border border-gray-200 p-4 sticky top-4">
									<h2 className="text-sm font-semibold text-gray-900 mb-3">Summary</h2>

									{/* Items count */}
									<p className="text-xs text-gray-500 mb-2">
										{cart?.cart_items?.length} item{cart?.cart_items?.length !== 1 ? "s" : ""}
									</p>

									{/* Price breakdown */}
									<div className="space-y-1.5 text-xs">
										<div className="flex justify-between text-gray-600">
											<span>Subtotal</span>
											<span>
												{getMoneyPrefix(currency)} {subtotal.toFixed(2)}
											</span>
										</div>
										{showShippingDetails && (
											<div className="flex justify-between text-gray-600">
												<span>Delivery</span>
												<span>
													{effectiveAddress
														? `${getMoneyPrefix(currency)} ${deliveryFee.toFixed(2)}`
														: "—"}
												</span>
											</div>
										)}
									</div>

									{/* Total */}
									<div className="border-t border-gray-200 mt-2.5 pt-2.5">
										<div className="flex justify-between text-sm">
											<span className="font-medium text-gray-900">Total</span>
											<span className="font-semibold text-akgreen">
												{getMoneyPrefix(currency)} {grandTotal}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Mobile Sticky Bottom Bar */}
					<div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-40 safe-area-inset-bottom">
						<div className="px-4 py-2.5">
							<div className="flex items-center justify-between gap-3">
								{/* Order Summary */}
								<div className="flex items-center gap-3">
									<div>
										<p className="text-xs text-gray-500">
											{cart?.cart_items?.length} item{cart?.cart_items?.length !== 1 ? "s" : ""}
										</p>
										<p className="text-base font-semibold text-gray-900">
											{getMoneyPrefix(currency)}
											{grandTotal}
										</p>
									</div>
									<button
										onClick={() => setShowMobileOrderSummary(true)}
										className="text-akgreen text-xs font-medium"
									>
										Details
									</button>
								</div>

								{/* Primary Action Button */}
								{showShippingDetails && currentStep === 1 ? (
									<button
										onClick={goToNextStep}
										disabled={!canProceedToPayment}
										className={`flex items-center gap-1.5 px-4 py-2.5 rounded-md font-medium text-sm ${
											canProceedToPayment
												? "bg-akgreen text-white"
												: "bg-gray-200 text-gray-400 cursor-not-allowed"
										}`}
									>
										<span>Continue</span>
										<FaArrowRight className="w-3 h-3" />
									</button>
								) : (
									<span className="text-xs text-gray-400">Select payment above</span>
								)}
							</div>
						</div>
					</div>

					{/* Mobile Order Summary Modal */}
					{showMobileOrderSummary && (
						<div className="fixed inset-0 bg-black/40 z-50 lg:hidden">
							<div className="absolute inset-0" onClick={() => setShowMobileOrderSummary(false)} />
							<div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[70vh] overflow-hidden">
								{/* Header */}
								<div className="flex items-center justify-between px-4 py-2.5 border-b">
									<h2 className="text-sm font-semibold text-gray-900">Order Summary</h2>
									<button
										onClick={() => setShowMobileOrderSummary(false)}
										className="text-gray-400 text-lg leading-none"
									>
										&times;
									</button>
								</div>

								<div className="overflow-y-auto max-h-[calc(70vh-90px)] p-4 space-y-3">
									{/* Delivery Address (if applicable) */}
									{showShippingDetails && effectiveAddress && currentStep === 2 && (
										<div className="flex items-start justify-between pb-3 border-b">
											<div>
												<p className="text-xs text-gray-500 mb-1">Delivering to</p>
												<p className="text-sm font-medium text-gray-900">
													{effectiveAddress.recipient_name}
												</p>
												<p className="text-xs text-gray-600">{effectiveAddress.city}</p>
											</div>
											<button
												onClick={() => {
													setShowMobileOrderSummary(false);
													goToPreviousStep();
												}}
												className="text-akgreen text-xs font-medium"
											>
												Change
											</button>
										</div>
									)}

									{/* Cart Items */}
									<div className="space-y-2">
										<div className="flex items-center justify-between text-sm">
											<span className="text-gray-700">{cart?.cart_items?.length} items</span>
											<Link
												to="/cart"
												className="text-akgreen text-xs"
												onClick={() => setShowMobileOrderSummary(false)}
											>
												Edit
											</Link>
										</div>
										{cart?.cart_items?.map((item) => (
											<div key={item.id} className="flex items-center gap-3 py-2">
												<div className="w-10 h-12 rounded bg-gray-100 overflow-hidden flex-shrink-0">
													{item.book_cover?.url ? (
														<img
															src={item.book_cover.url}
															alt=""
															className="w-full h-full object-cover"
														/>
													) : (
														<div className="w-full h-full bg-gray-200" />
													)}
												</div>
												<div className="flex-1 min-w-0">
													<p className="text-sm text-gray-900 truncate">{item.book_title}</p>
													<p className="text-xs text-gray-500">Qty: {item.quantity}</p>
												</div>
											</div>
										))}
									</div>

									{/* Price Breakdown */}
									<div className="pt-3 border-t space-y-2">
										<div className="flex justify-between text-sm">
											<span className="text-gray-600">Subtotal</span>
											<span>
												{getMoneyPrefix(currency)} {subtotal.toFixed(2)}
											</span>
										</div>
										{showShippingDetails && effectiveAddress && (
											<div className="flex justify-between text-sm">
												<span className="text-gray-600">Delivery</span>
												<span>
													{getMoneyPrefix(currency)} {deliveryFee.toFixed(2)}
												</span>
											</div>
										)}
										<div className="flex justify-between text-base font-semibold pt-2 border-t">
											<span>Total</span>
											<span className="text-akgreen">
												{getMoneyPrefix(currency)} {grandTotal}
											</span>
										</div>
									</div>
								</div>

								{/* Close Button */}
								<div className="p-3 border-t">
									<button
										onClick={() => setShowMobileOrderSummary(false)}
										className="w-full py-2.5 rounded-md bg-gray-100 text-gray-600 text-sm font-medium"
									>
										Close
									</button>
								</div>
							</div>
						</div>
					)}

					{/* Mobile Address Creation Modal */}
					{showAddNewDeliveryAddress && (
						<div className="fixed inset-0 bg-black/40 z-50 md:hidden">
							<div
								className="absolute inset-0"
								onClick={() => setShowAddNewDeliveryAddress(false)}
							></div>
							<div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[85vh] overflow-y-auto">
								<div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
									<h2 className="text-base font-semibold text-gray-900">Add Address</h2>
									<button
										onClick={() => setShowAddNewDeliveryAddress(false)}
										className="text-gray-400 text-lg"
									>
										×
									</button>
								</div>
								<div className="p-4">
									<ShippingDetails setShowAddNewDeliveryAddress={setShowAddNewDeliveryAddress} />
								</div>
							</div>
						</div>
					)}

					{/* Toast Notification */}
					{/* {showToast && (
						<Toast
							message={toastMessage}
							type="info"
							onClose={() => setShowToast(false)}
							duration={3000}
						/>
					)} */}

					<ModalNotification
						visible={showPaymentCompletedModal}
						setVisible={setShowPaymentCompletedModal}
						title="Payment successful!"
					>
						<div className="flex items-center gap-4 bg-green-50 p-6 rounded-xl">
							<div className="text-akgreen">
								<AiOutlineCheckCircle size={130} />
							</div>
							<div className="space-y-4">
								<p className="text-lg">
									Your <strong>{paymentMethod}</strong> payment was successful. Details are as
									follows:
								</p>
								<ul className="list-none space-y-2">
									<li className="flex items-center gap-2 text-akgreen">
										<span className="font-bold">Total payment:</span>
										<span>${Number(totalPaid).toFixed(2)}</span>
									</li>
									<li className="flex items-center gap-2 text-akgreen">
										<span className="font-bold">Number of books:</span>
										<span>{bookCount}</span>
									</li>
								</ul>
								<p>
									Go to{" "}
									<Link
										to="/my-library"
										className="text-akgreen font-bold hover:text-akgreener"
										onClick={() => setShowPaymentCompletedModal(false)}
									>
										My library
									</Link>{" "}
									to view purchased books
								</p>
							</div>
						</div>

						<div className="my-6 border-t border-gray-200" />

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							{bookItems?.map((bookItem) => (
								<BookInCart
									key={bookItem.id}
									title={bookItem?.book_title}
									coverUrl={bookItem?.book_cover?.url}
									author={bookItem?.author}
									quantity={bookItem?.quantity}
									publisher={bookItem?.user}
									price={(bookItem?.price * Number(currencyRate?.data?.rate || 1)).toFixed(2)}
									currencySymbol={bookItem?.currency_symbol}
									cartId={bookItem?.id}
									paymentResponse
								/>
							))}
						</div>

						<div className="mt-6 bg-gray-50 p-4 rounded-xl text-center">
							<p>
								Go to{" "}
								<Link
									to="/my-library"
									className="text-akgreen font-bold hover:text-akgreener"
									onClick={() => setShowPaymentCompletedModal(false)}
								>
									My library
								</Link>{" "}
								to view purchased books
							</p>
						</div>
					</ModalNotification>
				</PaddingX>
			</div>
		</Container>
	);
};

export default Checkout;
